'use client';

import { useConsent } from '@/components/consent/consent-context';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n/context';
import { Link } from '@/lib/i18n/link';

interface CookieRow {
  name: string;
  provider: string;
  duration: string;
  purpose: string;
}

interface CookieTable {
  heading: string;
  description: string;
  rows: CookieRow[];
}

const CONTENT = {
  en: {
    badge: 'Legal',
    title: 'Cookie Policy',
    lastUpdated: 'Last updated: August 22, 2026',
    intro:
      'This Cookie Policy explains which cookies and similar technologies OpenChat, Inc. ("Open," "we," "us") uses on www.open.cx, why we use them, and how you can control them. It supplements our',
    privacyPolicy: 'Privacy Policy',
    whatH: '1. What are cookies?',
    whatP:
      'Cookies are small text files stored on your device when you visit a website. We also use similar technologies such as tracking scripts and pixels; this policy refers to all of them as "cookies."',
    consentH: '2. Your choices',
    consentP1:
      'In jurisdictions that require opt-in consent (the EEA, the United Kingdom, and Switzerland), we do not set any non-essential cookies until you accept them in the consent banner. You can reject all non-essential cookies with one click, or enable categories individually.',
    consentP2:
      'You can change or withdraw your choice at any time via "Cookie settings" in the page footer. Your choice is stored for 6 months, after which we ask again. We also honor the Global Privacy Control (GPC) browser signal: when it is enabled, marketing cookies stay off regardless of other settings.',
    tableHeaderName: 'Cookie',
    tableHeaderProvider: 'Provider',
    tableHeaderDuration: 'Duration',
    tableHeaderPurpose: 'Purpose',
    tables: [
      {
        heading: '3. Strictly necessary',
        description:
          'These are needed for the site to work and remember your basic settings. They are always on and do not track you.',
        rows: [
          {
            name: 'ocx_consent',
            provider: 'Open',
            duration: '6 months',
            purpose: 'Stores your cookie consent choices.',
          },
          {
            name: 'NEXT_LOCALE',
            provider: 'Open',
            duration: '30 days',
            purpose: 'Remembers your language preference.',
          },
          {
            name: 'LOCALE_CHOICE',
            provider: 'Open',
            duration: '1 year',
            purpose:
              'Remembers that you chose a language yourself, so we stop suggesting another one.',
          },
          {
            name: 'VISITOR_COUNTRY / VISITOR_REGION / FROM_ARABIC_COUNTRY',
            provider: 'Open',
            duration: '30 days',
            purpose:
              'Approximate country (from your IP) used for localization and to apply the right consent rules for your jurisdiction.',
          },
        ],
      },
      {
        heading: '4. Analytics (only with your consent where required)',
        description:
          'These help us understand how the site is used so we can improve it. This category includes session replay (a recording of how you interact with pages).',
        rows: [
          {
            name: '_ga, _ga_*',
            provider: 'Google Analytics',
            duration: 'Up to 2 years',
            purpose:
              'Distinguishes visitors and sessions for usage statistics.',
          },
          {
            name: 'hubspotutk, __hstc, __hssc, __hssrc',
            provider: 'HubSpot',
            duration: 'Session to 6 months',
            purpose:
              'Visitor analytics and, when you submit a form, attribution of your visit history to your contact record.',
          },
          {
            name: 'ph_*',
            provider: 'PostHog (EU)',
            duration: 'Up to 1 year',
            purpose: 'Product analytics, error tracking, and session replay.',
          },
          {
            name: '(script identifiers)',
            provider: 'DataBuddy',
            duration: '—',
            purpose: 'Aggregate site analytics.',
          },
        ],
      },
      {
        heading: '5. Marketing (only with your consent where required)',
        description:
          'These support our advertising and help our marketing partners recognize visitors. In California-law terms, this category is what "Do Not Sell or Share My Personal Information" switches off.',
        rows: [
          {
            name: '_rdt_uuid',
            provider: 'Reddit Ads',
            duration: '3 months',
            purpose: 'Measures ad performance and enables retargeting.',
          },
          {
            name: 'unify_visitor_id, unify_session_id',
            provider: 'Unify Intent',
            duration: 'Up to 1 year',
            purpose: 'Identifies business visitors and buying intent.',
          },
          {
            name: '(script identifiers)',
            provider: 'RB2B',
            duration: '—',
            purpose: 'Identifies business visitors to our website.',
          },
          {
            name: '(script identifiers)',
            provider: 'Lemlist',
            duration: '—',
            purpose: 'Recognizes visitors arriving from our email campaigns.',
          },
        ],
      },
    ] as CookieTable[],
    browserH: '6. Browser controls',
    browserP:
      'Most browsers let you block or delete cookies in their settings. Blocking strictly necessary cookies may break parts of the site (for example, your language preference).',
    contactH: '7. Contact',
    contactP: 'Questions about this policy? Contact our Privacy Officer at',
    manageButton: 'Open cookie settings',
  },
  ar: {
    badge: 'قانوني',
    title: 'سياسة ملفات تعريف الارتباط',
    lastUpdated: 'آخر تحديث: 22 أغسطس 2026',
    intro:
      'توضّح سياسة ملفات تعريف الارتباط هذه ما تستخدمه OpenChat, Inc. ‏("Open" أو "نحن") من ملفات تعريف الارتباط والتقنيات المشابهة على www.open.cx، ولماذا نستخدمها، وكيف يمكنك التحكم بها. وهي مكمّلة لـ',
    privacyPolicy: 'سياسة الخصوصية',
    whatH: '١. ما هي ملفات تعريف الارتباط؟',
    whatP:
      'ملفات تعريف الارتباط هي ملفات نصية صغيرة تُخزَّن على جهازك عند زيارة موقع ما. كما نستخدم تقنيات مشابهة مثل نصوص التتبّع والبكسلات؛ وتشير هذه السياسة إليها جميعًا بمصطلح "ملفات تعريف الارتباط".',
    consentH: '٢. خياراتك',
    consentP1:
      'في الدول التي تشترط موافقة مسبقة (المنطقة الاقتصادية الأوروبية والمملكة المتحدة وسويسرا)، لا نضع أي ملفات تعريف ارتباط غير ضرورية حتى توافق عليها في شريط الموافقة. يمكنك رفض كل الملفات غير الضرورية بنقرة واحدة، أو تفعيل الفئات كلًّا على حدة.',
    consentP2:
      'يمكنك تغيير خيارك أو سحبه في أي وقت عبر "إعدادات ملفات تعريف الارتباط" في أسفل الصفحة. يُحفظ خيارك لمدة ٦ أشهر ثم نسألك مجددًا. كما نحترم إشارة Global Privacy Control ‏(GPC) في المتصفح: عند تفعيلها تبقى ملفات التسويق معطّلة بغض النظر عن الإعدادات الأخرى.',
    tableHeaderName: 'الملف',
    tableHeaderProvider: 'المزوّد',
    tableHeaderDuration: 'المدة',
    tableHeaderPurpose: 'الغرض',
    tables: [
      {
        heading: '٣. ضرورية للغاية',
        description:
          'هذه الملفات ضرورية لعمل الموقع وتذكّر إعداداتك الأساسية. وهي مفعّلة دائمًا ولا تتتبّعك.',
        rows: [
          {
            name: 'ocx_consent',
            provider: 'Open',
            duration: '٦ أشهر',
            purpose: 'يحفظ اختياراتك للموافقة على ملفات تعريف الارتباط.',
          },
          {
            name: 'NEXT_LOCALE',
            provider: 'Open',
            duration: '٣٠ يومًا',
            purpose: 'يتذكّر لغتك المفضّلة.',
          },
          {
            name: 'LOCALE_CHOICE',
            provider: 'Open',
            duration: 'سنة واحدة',
            purpose:
              'يتذكّر أنك اخترت اللغة بنفسك، فنتوقّف عن اقتراح لغة أخرى.',
          },
          {
            name: 'VISITOR_COUNTRY / VISITOR_REGION / FROM_ARABIC_COUNTRY',
            provider: 'Open',
            duration: '٣٠ يومًا',
            purpose:
              'الدولة التقريبية (من عنوان IP) لأغراض تخصيص اللغة وتطبيق قواعد الموافقة الملائمة لبلدك.',
          },
        ],
      },
      {
        heading: '٤. التحليلات (بموافقتك حيثما تُشترط)',
        description:
          'تساعدنا هذه الملفات على فهم كيفية استخدام الموقع لتحسينه. وتشمل هذه الفئة تسجيل الجلسات (تسجيل لكيفية تفاعلك مع الصفحات).',
        rows: [
          {
            name: '_ga, _ga_*',
            provider: 'Google Analytics',
            duration: 'حتى سنتين',
            purpose: 'يميّز الزوّار والجلسات لإحصاءات الاستخدام.',
          },
          {
            name: 'hubspotutk, __hstc, __hssc, __hssrc',
            provider: 'HubSpot',
            duration: 'من الجلسة حتى ٦ أشهر',
            purpose:
              'تحليلات الزوّار، وعند إرسالك نموذجًا، ربط سجلّ زيارتك بملف جهة الاتصال الخاص بك.',
          },
          {
            name: 'ph_*',
            provider: 'PostHog (الاتحاد الأوروبي)',
            duration: 'حتى سنة',
            purpose: 'تحليلات المنتج وتتبّع الأخطاء وتسجيل الجلسات.',
          },
          {
            name: '(معرّفات نصوص برمجية)',
            provider: 'DataBuddy',
            duration: '—',
            purpose: 'تحليلات إجمالية للموقع.',
          },
        ],
      },
      {
        heading: '٥. التسويق (بموافقتك حيثما تُشترط)',
        description:
          'تدعم هذه الملفات إعلاناتنا وتساعد شركاءنا التسويقيين على التعرّف على الزوّار. ووفق قانون كاليفورنيا، هذه هي الفئة التي يعطّلها خيار "عدم بيع أو مشاركة معلوماتي الشخصية".',
        rows: [
          {
            name: '_rdt_uuid',
            provider: 'Reddit Ads',
            duration: '٣ أشهر',
            purpose: 'يقيس أداء الإعلانات ويتيح إعادة الاستهداف.',
          },
          {
            name: 'unify_visitor_id, unify_session_id',
            provider: 'Unify Intent',
            duration: 'حتى سنة',
            purpose: 'يحدّد هوية الزوّار من الشركات ونيّة الشراء.',
          },
          {
            name: '(معرّفات نصوص برمجية)',
            provider: 'RB2B',
            duration: '—',
            purpose: 'يحدّد هوية زوّار موقعنا من الشركات.',
          },
          {
            name: '(معرّفات نصوص برمجية)',
            provider: 'Lemlist',
            duration: '—',
            purpose: 'يتعرّف على الزوّار القادمين من حملاتنا البريدية.',
          },
        ],
      },
    ] as CookieTable[],
    browserH: '٦. إعدادات المتصفح',
    browserP:
      'تتيح لك معظم المتصفحات حظر ملفات تعريف الارتباط أو حذفها من إعداداتها. وقد يؤدي حظر الملفات الضرورية إلى تعطّل أجزاء من الموقع (مثل تفضيل اللغة).',
    contactH: '٧. التواصل',
    contactP: 'لديك أسئلة حول هذه السياسة؟ تواصل مع مسؤول الخصوصية لدينا على',
    manageButton: 'فتح إعدادات ملفات تعريف الارتباط',
  },
};

const CookiePolicyPage = () => {
  const { locale } = useTranslation();
  const { openPreferences } = useConsent();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <section className="py-32">
      <div className="container max-w-4xl">
        <div className="mb-12 flex flex-col gap-6">
          <Badge variant="outline" className="w-fit text-black">
            {c.badge}
          </Badge>
          <h1 className="font-serif text-4xl text-black md:text-5xl">
            {c.title}
          </h1>
          <p className="text-muted-foreground text-sm">{c.lastUpdated}</p>
          <p className="max-w-3xl text-base leading-relaxed text-neutral-700">
            {c.intro}{' '}
            <Link
              href="/privacy-policy"
              className="text-primary hover:underline"
            >
              {c.privacyPolicy}
            </Link>
            .
          </p>
          <button
            onClick={openPreferences}
            className="bg-foreground text-background hover:bg-foreground/90 w-fit rounded-full px-5 py-2.5 text-sm font-medium transition-all hover:shadow-md active:scale-[0.98]"
          >
            {c.manageButton}
          </button>
        </div>

        <div className="prose prose-sm prose-slate max-w-none text-black">
          <h2>{c.whatH}</h2>
          <p>{c.whatP}</p>

          <h2>{c.consentH}</h2>
          <p>{c.consentP1}</p>
          <p>{c.consentP2}</p>

          {c.tables.map((table) => (
            <div key={table.heading}>
              <h2>{table.heading}</h2>
              <p>{table.description}</p>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="border-b text-start">
                      <th className="py-2 pe-4 text-start">
                        {c.tableHeaderName}
                      </th>
                      <th className="py-2 pe-4 text-start">
                        {c.tableHeaderProvider}
                      </th>
                      <th className="py-2 pe-4 text-start">
                        {c.tableHeaderDuration}
                      </th>
                      <th className="py-2 text-start">
                        {c.tableHeaderPurpose}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows.map((row) => (
                      <tr
                        key={`${row.provider}-${row.name}`}
                        className="border-b align-top"
                      >
                        <td className="py-2 pe-4 font-mono text-xs">
                          {row.name}
                        </td>
                        <td className="py-2 pe-4">{row.provider}</td>
                        <td className="py-2 pe-4">{row.duration}</td>
                        <td className="py-2">{row.purpose}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <h2>{c.browserH}</h2>
          <p>{c.browserP}</p>

          <h2>{c.contactH}</h2>
          <p>
            {c.contactP}{' '}
            <a
              href="mailto:privacy@open.cx"
              className="text-primary hover:underline"
            >
              privacy@open.cx
            </a>
            .
          </p>
        </div>
      </div>
    </section>
  );
};

export default CookiePolicyPage;
